import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { getOgImage } from '@/config/ogImages';
import LocalizedLink from '@/i18n/LocalizedLink';
import { useLang } from '@/i18n/useLocalizedPath';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, ArrowRight, Building, Loader2, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventsHomePage() {
  const { t } = useTranslation();
  const lang = useLang();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/events?lang=${lang}`);
        setCities(response.data.cities);
      } catch (err) {
        console.error('Error fetching cities:', err);
        setError(t('eventsHome.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, [t, lang]);

  return (
    <MainLayout>
      <SEOHead
        title="Events Hub | Discover B2B Events Across the UK | Jigsaw"
        description="Browse conferences, exhibitions & trade shows across the UK. Get AI-powered event insights and free group accommodation for your team."
        url="https://events.jigsawconferences.co.uk/events"
        image={getOgImage('events')}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 to-blue-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="events-home-title">
              {t('eventsHome.heroTitle')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('eventsHome.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LocalizedLink to="/events/list-your-event">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg rounded-lg font-medium" data-testid="list-event-cta-button">
                  {t('eventsHome.listFree')}
                </Button>
              </LocalizedLink>
              <Button
                variant="outline"
                className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg rounded-lg font-medium"
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                data-testid="accommodation-cta-button"
              >
                {t('eventsHome.needAccommodation')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('eventsHome.helpTitle')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('eventsHome.helpSubtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <Building className="w-12 h-12 text-orange-600 mb-4" />
                <CardTitle>{t('eventsHome.venueTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{t('eventsHome.venueDesc')}</p>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <MapPin className="w-12 h-12 text-orange-600 mb-4" />
                <CardTitle>{t('eventsHome.accomTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{t('eventsHome.accomDesc')}</p>
              </CardContent>
            </Card>

            <Card className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <Calendar className="w-12 h-12 text-orange-600 mb-4" />
                <CardTitle>{t('eventsHome.planTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{t('eventsHome.planDesc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('eventsHome.citiesTitle')}</h2>
            <p className="text-lg text-gray-600">{t('eventsHome.citiesSubtitle')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">{t('eventsHome.loadingEvents')}</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {cities.map((city) => (
                <LocalizedLink key={city.slug} to={`/events/${city.slug}`} data-testid={`city-card-${city.slug}`}>
                  <Card className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-shadow h-full cursor-pointer">
                    <CardHeader>
                      <CardTitle className="text-2xl text-gray-900">{city.name}</CardTitle>
                      <CardDescription className="text-gray-600">
                        {city.totalEvents} {city.totalEvents === 1 ? t('eventsHome.eventOne') : t('eventsHome.eventOther')} • {city.venues.length} {city.venues.length === 1 ? t('eventsHome.venueOne') : t('eventsHome.venueOther')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{city.intro}</p>
                      <div className="flex items-center text-orange-600 font-medium">
                        {t('eventsHome.viewEvents')} <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </CardContent>
                  </Card>
                </LocalizedLink>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Event Boost CTA */}
      <section className="py-12 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('eventsHome.boostTitle')}</h3>
                  <p className="text-gray-600">{t('eventsHome.boostDesc')}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <LocalizedLink to="/boost/dashboard">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
                    {t('eventsHome.boostCta')}
                  </Button>
                </LocalizedLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('eventsHome.ctaTitle')}</h2>
          <p className="text-xl mb-8 text-orange-100">{t('eventsHome.ctaSubtitle')}</p>
          <LocalizedLink to="/events/list-your-event">
            <Button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg font-medium" data-testid="list-event-footer-button">
              {t('eventsHome.ctaButton')}
            </Button>
          </LocalizedLink>
        </div>
      </section>
    </MainLayout>
  );
}
